/**
 * ScheduleRunDetail
 *
 * Per-user breakdown for a single schedule run.
 * Shown inside ScheduleRunHistory when the user clicks [详情].
 * Polls every 3s while status = 'running'.
 */

import React, { useState } from 'react';
import { ExternalLink, RefreshCw, RotateCcw, StopCircle } from 'lucide-react';
import { RunStatusBadge } from './RunStatusBadge';
import { useScheduleRunDetail, useRetryFailedRun, useCancelRun } from '../hooks/useSchedules';
import { useUsersBatch } from '@/features/image-workflows/hooks/useUsersBatch';
import { formatDuration } from '@/lib/format/duration';
import type { ScheduleRunUserResult } from '@/types/schedule';

interface Props {
  scheduleId: string;
  runId: string;
  onRetried?: () => void;
}

export function ScheduleRunDetail({ scheduleId, runId, onRetried }: Props) {
  const { data: run, isLoading } = useScheduleRunDetail(scheduleId, runId);
  const retryMutation = useRetryFailedRun();
  const cancelMutation = useCancelRun();
  const [expandedErrorUserId, setExpandedErrorUserId] = useState<string | null>(null);

  const userIds = run?.users?.map((u: ScheduleRunUserResult) => u.user_id) ?? [];
  const { users: userInfoMap } = useUsersBatch(userIds);

  if (isLoading || !run) {
    return <div className="text-sm text-gray-500 py-4 text-center">加载中...</div>;
  }

  const isStaleRunning =
    run.status === 'running' &&
    run.started_at != null &&
    Date.now() - new Date(run.started_at).getTime() > 2 * 60 * 60 * 1000;

  const handleRetryAll = async () => {
    try {
      await retryMutation.mutateAsync({ scheduleId, runId });
      onRetried?.();
    } catch {
      // error surfaced by TanStack Query
    }
  };

  const handleCancel = async () => {
    if (!confirm('确定要终止此次运行？已完成的用户不受影响。')) return;
    try {
      await cancelMutation.mutateAsync({ scheduleId, runId });
    } catch {
      // error surfaced by TanStack Query
    }
  };

  return (
    <div className="space-y-3">
      {/* Run summary header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <RunStatusBadge status={run.status} />
          <span className="text-sm text-gray-600">
            {run.completed_users} 成功 / {run.failed_users} 失败 / {run.total_users} 总计
          </span>
          {run.duration_seconds !== null && (
            <span className="text-xs text-gray-400">{formatDuration(run.duration_seconds)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {run.execution_id && run.workflow_id && (
            <a
              href={`/#/image-workflows?workflowId=${run.workflow_id}&executionId=${run.execution_id}`}
              className="flex items-center gap-1 text-xs px-2 py-1 border border-blue-200 text-blue-600 rounded hover:bg-blue-50"
            >
              <ExternalLink className="w-3 h-3" />
              查看执行详情
            </a>
          )}
          {(run.status === 'partial' || run.status === 'failed') && (
            <button
              className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              onClick={handleRetryAll}
              disabled={retryMutation.isPending}
            >
              <RotateCcw className="w-3 h-3" />
              重跑未完成用户
            </button>
          )}
          {(run.status === 'running' || run.status === 'pending') && (
            <button
              className="flex items-center gap-1 text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              <StopCircle className="w-3 h-3" />
              终止运行
            </button>
          )}
        </div>
      </div>

      {/* Running progress bar */}
      {run.status === 'running' && !isStaleRunning && (
        <div className="space-y-1">
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{
                width: `${
                  run.total_users > 0
                    ? ((run.completed_users + run.failed_users) / run.total_users) * 100
                    : 0
                }%`,
              }}
            />
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" />
            已完成 {run.completed_users + run.failed_users} / {run.total_users}
          </p>
        </div>
      )}

      {isStaleRunning && (
        <p className="text-xs text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
          ⚠️ 运行超时，请联系管理员
        </p>
      )}

      {/* Workflow-level error */}
      {run.error && (
        <p className="text-xs text-red-700 bg-red-50 px-3 py-2 rounded">{run.error}</p>
      )}

      {/* Per-user breakdown */}
      {run.users.length > 0 && (
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
            用户明细
          </div>
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {run.users.map((u: ScheduleRunUserResult) => (
              <React.Fragment key={u.user_id}>
                <div className="flex items-center gap-2 px-3 py-2 text-xs">
                  <RunStatusBadge status={u.status} />
                  <span className="text-gray-700 truncate flex-1" title={u.user_id}>
                    {userInfoMap?.[u.user_id]?.nickname || `${u.user_id.slice(0, 8)}…`}
                  </span>
                  <span className="text-gray-400 shrink-0">{formatDuration(u.duration_seconds)}</span>
                  {u.error && (
                    <button
                      className="text-red-600 text-left truncate max-w-[200px] hover:underline"
                      title="点击展开"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedErrorUserId(expandedErrorUserId === u.user_id ? null : u.user_id);
                      }}
                    >
                      {u.error}
                    </button>
                  )}
                </div>
                {expandedErrorUserId === u.user_id && u.error && (
                  <div className="px-3 py-2 bg-red-50 text-xs text-red-700 whitespace-pre-wrap break-all border-t border-red-100">
                    {u.error}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
